import { useCallback, useEffect, useRef, useState } from "react";
import { BuilderState, initialState, ElStyle, Theme } from "./state";
import { BlockType, Block } from "../blockModel/types";
import {
	addBlock as pureAddBlock,
	addBlockAt as pureAddBlockAt,
	delBlock as pureDelBlock,
	moveBlock as pureMoveBlock,
	dupBlock as pureDupBlock,
	addRow as pureAddRow,
	delRow as pureDelRow,
	dupRow as pureDupRow,
	moveRow as pureMoveRow,
	setBlockStyle as pureSetBlockStyle,
	setElStyle as pureSetElStyle,
	setTheme as pureSetTheme,
	setBlockData as pureSetBlockData,
	setRowText as pureSetRowText,
	setRowInline as pureSetRowInline,
	mergeRow as pureMergeRow,
	splitRow as pureSplitRow,
	addHdrLine as pureAddHdrLine,
	delHdrLine as pureDelHdrLine,
	applyPresentation as pureApplyPresentation,
	addSectionHeader as pureAddSectionHeader,
	addSectionAt as pureAddSectionAt,
	toggleRowEmphasis as pureToggleRowEmphasis,
	isSkeletonEmail,
	toContent,
	missingRequiredVars,
	PresentationProfile,
} from "./actions";
import { EMAIL_PRESETS, STANDARD_FULL_LAYOUT } from "./emailPresets";
import { getVarCatalog, VarDef } from "./emailVars";

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

export type Mode = "edit" | "preview";

export interface EmailBuilderActions {
	/** Replace the entire canvas content with the given markup — pushes onto undo history. */
	loadContent: (markup: string) => void;
	/** Apply a presentation-only profile (column mode, density, optional brand color) without touching any text. */
	applyPresentation: (profile: PresentationProfile) => void;
	/**
	 * Apply a named preset. When the canvas is a skeleton (no summary block), first builds
	 * the full standard layout then applies the preset's presentation profile. Otherwise
	 * only restyles the existing content.
	 */
	applyPreset: (presetId: string) => void;
	/** Append a section header raw row ("# New section") to a summary block. */
	addSectionHeader: (blockId: string) => void;
	/** Insert a section header raw row into a summary at a given row index (palette drop). */
	addSectionAt: (blockId: string, index: number) => void;
	/** Toggle emphasis (bold/big) on the left or right side of a summary row. */
	toggleRowEmphasis: (blockId: string, index: number, side: "left" | "right") => void;
	addBlock: (type: BlockType) => void;
	addBlockAt: (type: BlockType, beforeId: string | null) => void;
	delBlock: (id: string) => void;
	moveBlock: (fromId: string, beforeId: string | null) => void;
	dupBlock: (id: string) => void;
	addRow: (blockId: string) => void;
	delRow: (blockId: string, index: number) => void;
	dupRow: (blockId: string, index: number) => void;
	moveRow: (blockId: string, from: number, to: number) => void;
	setBlockStyle: (blockId: string, patch: Block["style"]) => void;
	setElStyle: (elKey: string, patch: ElStyle) => void;
	setTheme: (patch: Partial<Theme>) => void;
	setBlockData: (blockId: string, patch: Partial<Block["data"]>) => void;
	setRowText: (blockId: string, field: string, index: number, value: string) => void;
	setRowInline: (blockId: string, index: number, value: boolean) => void;
	mergeRow: (blockId: string, index: number) => void;
	splitRow: (blockId: string, index: number) => void;
	/** Append an empty freeform line to the header block's data.lines[]. */
	addHdrLine: (blockId: string) => void;
	/** Remove the header line at the given index. */
	delHdrLine: (blockId: string, index: number) => void;
	setSel: (id: string | null) => void;
	setSelEl: (elKey: string | null) => void;
	setMode: (mode: Mode) => void;
	toggleDark: () => void;
	toggleSample: () => void;
	undo: () => void;
	redo: () => void;
	getContent: () => string;
	missing: () => string[];
}

export interface EmailBuilderResult {
	state: BuilderState;
	catalog: VarDef[];
	mode: Mode;
	darkMode: boolean;
	sampleMode: boolean;
	canUndo: boolean;
	canRedo: boolean;
	actions: EmailBuilderActions;
}

// ---------------------------------------------------------------------------
// Internal history — kept in a ref so mutation never causes extra renders.
// Only the current BuilderState and the two boolean flags (canUndo/canRedo)
// are surfaced as React state.
// ---------------------------------------------------------------------------

interface History {
	snapshots: BuilderState[];
	pointer: number;
}

function makeHistory(s: BuilderState): History {
	return { snapshots: [s], pointer: 0 };
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useEmailBuilder(initialContent: string, group: string): EmailBuilderResult {
	const [builderState, setBuilderState] = useState<BuilderState>(() => initialState(initialContent));
	const [mode, setModeState] = useState<Mode>("edit");
	const [darkMode, setDarkMode] = useState(false);
	const [sampleMode, setSampleMode] = useState(false);
	// canUndo/canRedo are cheap booleans; store as state so components re-render correctly.
	const [canUndo, setCanUndo] = useState(false);
	const [canRedo, setCanRedo] = useState(false);

	// History lives entirely in a ref — no spurious renders from pointer moves.
	const historyRef = useRef<History>(makeHistory(initialState(initialContent)));

	// Stable ref to current builderState so callbacks never close over stale values.
	const builderRef = useRef(builderState);
	builderRef.current = builderState;

	// Reset everything when the template (initialContent) changes.
	const prevContentRef = useRef(initialContent);
	useEffect(() => {
		if (prevContentRef.current === initialContent) return;
		prevContentRef.current = initialContent;
		// Skip echo-reset: when our own change round-trips through the parent (onChange →
		// setContent → content prop), the incoming content equals what the builder already
		// holds. Rebuilding then would regenerate block ids, drop selection and remount the
		// contentEditables (React "removeChild" crash). Only reset on a genuine external change.
		if (toContent(builderRef.current).trim() === String(initialContent || "").trim()) return;
		const fresh = initialState(initialContent);
		historyRef.current = makeHistory(fresh);
		builderRef.current = fresh;
		setBuilderState(fresh);
		setCanUndo(false);
		setCanRedo(false);
	}, [initialContent]);

	const catalog: VarDef[] = getVarCatalog(group);

	// ---------------------------------------------------------------------------
	// Commit a new state — push onto history, sync canUndo/canRedo flags.
	// ---------------------------------------------------------------------------
	const commit = useCallback((next: BuilderState) => {
		const h = historyRef.current;
		// Dedupe: skip if JSON-identical to current snapshot.
		if (JSON.stringify(h.snapshots[h.pointer]) === JSON.stringify(next)) return;
		// Truncate redo branch and push.
		const snapshots = [...h.snapshots.slice(0, h.pointer + 1), next];
		const pointer = snapshots.length - 1;
		historyRef.current = { snapshots, pointer };
		builderRef.current = next;
		setBuilderState(next);
		setCanUndo(pointer > 0);
		setCanRedo(false); // just pushed — no redo available
	}, []);

	// ---------------------------------------------------------------------------
	// Actions
	// ---------------------------------------------------------------------------

	const actions: EmailBuilderActions = {
		loadContent: useCallback(
			(markup: string) => {
				const fresh = initialState(markup);
				// Preserve theme from current state so the brand color isn't reset.
				commit({ ...fresh, theme: builderRef.current.theme });
			},
			[commit]
		),

		applyPresentation: useCallback(
			(profile: PresentationProfile) => {
				commit(pureApplyPresentation(builderRef.current, profile));
			},
			[commit]
		),

		applyPreset: useCallback(
			(presetId: string) => {
				const preset = EMAIL_PRESETS.find((p) => p.id === presetId);
				if (!preset) return;
				const base = isSkeletonEmail(builderRef.current)
					? { ...initialState(STANDARD_FULL_LAYOUT), theme: builderRef.current.theme }
					: builderRef.current;
				commit(pureApplyPresentation(base, preset.profile));
			},
			[commit]
		),

		addSectionHeader: useCallback(
			(blockId: string) => {
				commit(pureAddSectionHeader(builderRef.current, blockId));
			},
			[commit]
		),

		addSectionAt: useCallback(
			(blockId: string, index: number) => {
				commit(pureAddSectionAt(builderRef.current, blockId, index));
			},
			[commit]
		),

		toggleRowEmphasis: useCallback(
			(blockId: string, index: number, side: "left" | "right") => {
				commit(pureToggleRowEmphasis(builderRef.current, blockId, index, side));
			},
			[commit]
		),

		addBlock: useCallback(
			(type: BlockType) => {
				commit(pureAddBlock(builderRef.current, type));
			},
			[commit]
		),

		addBlockAt: useCallback(
			(type: BlockType, beforeId: string | null) => {
				commit(pureAddBlockAt(builderRef.current, type, beforeId));
			},
			[commit]
		),

		delBlock: useCallback(
			(id: string) => {
				commit(pureDelBlock(builderRef.current, id));
			},
			[commit]
		),

		moveBlock: useCallback(
			(fromId: string, beforeId: string | null) => {
				commit(pureMoveBlock(builderRef.current, fromId, beforeId));
			},
			[commit]
		),

		dupBlock: useCallback(
			(id: string) => {
				commit(pureDupBlock(builderRef.current, id));
			},
			[commit]
		),

		addRow: useCallback(
			(blockId: string) => {
				commit(pureAddRow(builderRef.current, blockId));
			},
			[commit]
		),

		delRow: useCallback(
			(blockId: string, index: number) => {
				commit(pureDelRow(builderRef.current, blockId, index));
			},
			[commit]
		),

		dupRow: useCallback(
			(blockId: string, index: number) => {
				commit(pureDupRow(builderRef.current, blockId, index));
			},
			[commit]
		),

		moveRow: useCallback(
			(blockId: string, from: number, to: number) => {
				commit(pureMoveRow(builderRef.current, blockId, from, to));
			},
			[commit]
		),

		setBlockStyle: useCallback(
			(blockId: string, patch: Block["style"]) => {
				commit(pureSetBlockStyle(builderRef.current, blockId, patch));
			},
			[commit]
		),

		setElStyle: useCallback(
			(elKey: string, patch: ElStyle) => {
				commit(pureSetElStyle(builderRef.current, elKey, patch));
			},
			[commit]
		),

		setTheme: useCallback(
			(patch: Partial<Theme>) => {
				commit(pureSetTheme(builderRef.current, patch));
			},
			[commit]
		),

		setBlockData: useCallback(
			(blockId: string, patch: Partial<Block["data"]>) => {
				commit(pureSetBlockData(builderRef.current, blockId, patch));
			},
			[commit]
		),

		setRowText: useCallback(
			(blockId: string, field: string, index: number, value: string) => {
				commit(pureSetRowText(builderRef.current, blockId, field, index, value));
			},
			[commit]
		),

		setRowInline: useCallback(
			(blockId: string, index: number, value: boolean) => {
				commit(pureSetRowInline(builderRef.current, blockId, index, value));
			},
			[commit]
		),

		mergeRow: useCallback(
			(blockId: string, index: number) => {
				commit(pureMergeRow(builderRef.current, blockId, index));
			},
			[commit]
		),

		splitRow: useCallback(
			(blockId: string, index: number) => {
				commit(pureSplitRow(builderRef.current, blockId, index));
			},
			[commit]
		),

		addHdrLine: useCallback(
			(blockId: string) => {
				commit(pureAddHdrLine(builderRef.current, blockId));
			},
			[commit]
		),

		delHdrLine: useCallback(
			(blockId: string, index: number) => {
				commit(pureDelHdrLine(builderRef.current, blockId, index));
			},
			[commit]
		),

		// Selection is ephemeral UI state — does NOT push onto undo history.
		setSel: useCallback((id: string | null) => {
			setBuilderState((prev) => {
				const next = { ...prev, selId: id, selEl: null };
				builderRef.current = next;
				return next;
			});
		}, []),

		setSelEl: useCallback((elKey: string | null) => {
			setBuilderState((prev) => {
				const next = { ...prev, selEl: elKey };
				builderRef.current = next;
				return next;
			});
		}, []),

		setMode: useCallback((m: Mode) => {
			setModeState(m);
		}, []),

		toggleDark: useCallback(() => {
			setDarkMode((prev) => !prev);
		}, []),

		toggleSample: useCallback(() => {
			setSampleMode((prev) => !prev);
		}, []),

		undo: useCallback(() => {
			const h = historyRef.current;
			if (h.pointer <= 0) return;
			const newPointer = h.pointer - 1;
			// Preserve live selection — undo restores content but not cursor position.
			const { selId, selEl } = builderRef.current;
			const target = { ...h.snapshots[newPointer], selId, selEl };
			historyRef.current = { ...h, pointer: newPointer };
			builderRef.current = target;
			setBuilderState(target);
			setCanUndo(newPointer > 0);
			setCanRedo(true);
		}, []),

		redo: useCallback(() => {
			const h = historyRef.current;
			if (h.pointer >= h.snapshots.length - 1) return;
			const newPointer = h.pointer + 1;
			// Preserve live selection — redo restores content but not cursor position.
			const { selId, selEl } = builderRef.current;
			const target = { ...h.snapshots[newPointer], selId, selEl };
			historyRef.current = { ...h, pointer: newPointer };
			builderRef.current = target;
			setBuilderState(target);
			setCanUndo(true);
			setCanRedo(newPointer < h.snapshots.length - 1);
		}, []),

		getContent: useCallback(() => toContent(builderRef.current), []),

		missing: useCallback(() => missingRequiredVars(builderRef.current), []),
	};

	return {
		state: builderState,
		catalog,
		mode,
		darkMode,
		sampleMode,
		canUndo,
		canRedo,
		actions,
	};
}
